import { ExpertProfileEducationDto, ExpertProfileType } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { ErrorMessage } from '@components/shared/error-message.component';
import { FormField } from '@components/shared/form-field.component';
import { FormLabel } from '@components/shared/form-label.component';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { MultiSelect } from '@components/ui/multi-select';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { useGetCommunities } from '@queries/community.query';
import { XIcon } from 'lucide-react';
import { FunctionComponent, useMemo } from 'react';
import { ExpertProfileComponentProps } from './expert-profile-modal.component';

const years = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);

export const ExpertProfileModalBackgroundStep: FunctionComponent<ExpertProfileComponentProps> = (props) => {
  const { formData, setFormData, errors } = props;

  const { data: communitiesData, isLoading: isLoadingCommunities } = useGetCommunities();

  const communityOptions = useMemo(() => {
    if (communitiesData) {
      return communitiesData.map((community) => ({
        label: community.name,
        value: community.id,
      }));
    }
    return [];
  }, [communitiesData]);

  const handleFunctionalCommunitiesChange = (selectedValues: string[]) => {
    setFormData((prev) => ({
      ...prev,
      communities: selectedValues,
    }));
  };

  const handleAddEducation = () => {
    const newEducations = [
      ...(formData.educations || []),
      { title: '', institute: '', startYear: undefined, endYear: undefined },
    ];
    setFormData((prev) => ({ ...prev, educations: newEducations }));
  };

  const handleRemoveEducation = (index: number) => {
    const newEducations = [...(formData.educations || [])];
    newEducations.splice(index, 1);
    setFormData((prev) => ({ ...prev, educations: newEducations }));
  };

  const handleEducationChange = (index: number, field: keyof ExpertProfileEducationDto, value: string | number) => {
    const newEducations = [...(formData.educations || [])];
    const educationItem: ExpertProfileEducationDto = { ...newEducations[index] };

    if (field === 'startYear' || field === 'endYear') {
      const yearValue = value === '0' ? undefined : Number(value);
      educationItem[field] = yearValue;
      const { startYear, endYear } = educationItem;

      if (startYear !== undefined && endYear !== undefined && startYear > endYear) {
        if (field === 'startYear') {
          educationItem.endYear = startYear;
        } else if (field === 'endYear') {
          educationItem.startYear = endYear;
        }
      }
    } else {
      educationItem[field] = value as string;
    }
    newEducations[index] = educationItem;
    setFormData((prev) => ({ ...prev, educations: newEducations }));
  };

  const handleYearsOfExperienceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    const years = parseInt(numericValue, 10);
    setFormData((prev) => ({ ...prev, yearsOfExperience: isNaN(years) ? undefined : years }));
  };

  return (
    <div className="flex flex-col gap-6">
      {formData.type === ExpertProfileType.VETERAN && (
        <FormField>
          <FormLabel label="Functional Communities" />
          <MultiSelect
            options={communityOptions}
            onValueChange={handleFunctionalCommunitiesChange}
            placeholder="Select communities"
            value={formData.communities || []}
            className="bg-gray-100"
            disabled={isLoadingCommunities}
          />
          <ErrorMessage zodError={errors.communities} />
        </FormField>
      )}

      <FormField>
        <FormLabel label="Years of Experience" />
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          name="yearsOfExperience"
          min={0}
          value={formData.yearsOfExperience || ''}
          onChange={handleYearsOfExperienceChange}
          placeholder="Type a number"
          className="bg-gray-100"
        />
        <ErrorMessage zodError={errors.yearsOfExperience} />
      </FormField>

      <div>
        <div className="flex justify-between items-center mb-2">
          <FormLabel label="Education" />
        </div>
        <div className="flex flex-col gap-4">
          {formData.educations?.map((edu, index) => (
            <div
              key={index}
              className="p-4 border border-gray-200 rounded-md flex flex-col gap-4"
            >
              <div className="flex items-start gap-2">
                <FormField className="grow">
                  <Input
                    name={`educations[${index}].title`}
                    value={edu.title}
                    onChange={(e) => handleEducationChange(index, 'title', e.target.value)}
                    placeholder="Degree or Certificate"
                    className="bg-gray-100"
                  />
                  <ErrorMessage zodError={errors.educations?.[index]?.title} />
                </FormField>
                <IconButton
                  icon={<XIcon />}
                  onClick={() => handleRemoveEducation(index)}
                  variant="secondary"
                  ariaLabel="Remove education"
                  size="lg"
                />
              </div>

              <div className="flex items-start gap-2">
                <FormField className="grow w-full">
                  <Input
                    name={`educations[${index}].institute`}
                    value={edu.institute}
                    onChange={(e) => handleEducationChange(index, 'institute', e.target.value)}
                    placeholder="Institute"
                    className="bg-gray-100 w-full"
                  />
                  <ErrorMessage zodError={errors.educations?.[index]?.institute} />
                </FormField>
                <div className="min-w-20">
                  <Select
                    value={edu.startYear?.toString() || '0'}
                    onValueChange={(currentValue) => handleEducationChange(index, 'startYear', currentValue)}
                  >
                    <SelectTrigger className="bg-gray-100 w-full">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="0">Year</SelectItem>
                        {years.map((year) => (
                          <SelectItem
                            key={year}
                            value={year.toString()}
                          >
                            {year}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-20">
                  <Select
                    value={edu.endYear?.toString() || '0'}
                    onValueChange={(currentValue) => handleEducationChange(index, 'endYear', currentValue)}
                  >
                    <SelectTrigger className="bg-gray-100 w-full">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="0">Year</SelectItem>
                        {years.map((year) => (
                          <SelectItem
                            key={year}
                            value={year.toString()}
                          >
                            {year}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="link"
          onClick={handleAddEducation}
          className="text-primary py-2 h-auto"
        >
          + Add Education
        </Button>
      </div>
    </div>
  );
};
